import React, { useState, useEffect } from 'react';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyEuroIcon,
  ExclamationCircleIcon,
  ClockIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

// Données de démonstration pour les statistiques
const MOCK_STATS = {
  totalValue: 45672.89,
  itemCount: 127,
  averagePrice: 359.63,
  lowStockItems: 8,
  outOfStockItems: 3,
  topSellingCategories: [
    { name: 'Planches', percentage: 35, value: 15985.50 },
    { name: 'Voiles', percentage: 28, value: 12788.40 },
    { name: 'Combinaisons', percentage: 15, value: 6850.93 },
    { name: 'Accessoires', percentage: 12, value: 5480.75 },
    { name: 'Autres', percentage: 10, value: 4567.29 }
  ],
  stockRotation: [
    { name: 'Élevée (< 30 jours)', count: 42, value: 15528.78 },
    { name: 'Moyenne (30-90 jours)', count: 65, value: 25120.09 },
    { name: 'Faible (> 90 jours)', count: 20, value: 5024.02 }
  ],
  monthlyTrends: [
    { month: 'Jan', value: 41200 },
    { month: 'Fév', value: 42500 },
    { month: 'Mar', value: 44700 },
    { month: 'Avr', value: 46200 },
    { month: 'Mai', value: 45800 },
    { month: 'Juin', value: 45672 }
  ]
};

// Données de démonstration pour les produits à réapprovisionner
const MOCK_RESTOCK_ITEMS = [
  { id: 1, name: 'Voile Freeride 7.5', sku: 'VL-075', category: 'Voiles', current: 3, minimum: 5, suggested: 10, lastOrder: '2023-03-15' },
  { id: 3, name: 'Combinaison néoprène 3/2', sku: 'CB-32', category: 'Vêtements', current: 0, minimum: 3, suggested: 8, lastOrder: '2023-02-20' },
  { id: 5, name: 'Mât carbone 100%', sku: 'MT-100', category: 'Gréements', current: 7, minimum: 8, suggested: 5, lastOrder: '2023-04-10' }
];

// Données pour les rapports disponibles
const MOCK_REPORTS = [
  { id: 1, name: 'Valorisation des stocks', description: 'Rapport détaillé de la valeur des stocks par catégorie', format: 'Excel', lastGenerated: '2023-06-01' },
  { id: 2, name: 'Analyse de rotation', description: 'Analyse des taux de rotation par produit avec recommandations', format: 'PDF', lastGenerated: '2023-06-05' },
  { id: 3, name: 'Produits dormants', description: 'Liste des produits sans mouvement depuis plus de 90 jours', format: 'Excel', lastGenerated: '2023-06-01' },
  { id: 4, name: 'Historique des prix', description: 'Évolution des prix d\'achat et de vente sur 12 mois', format: 'Excel', lastGenerated: '2023-05-15' }
];

const InventoryAnalytics = () => {
  const [stats, setStats] = useState(MOCK_STATS);
  const [restockItems, setRestockItems] = useState(MOCK_RESTOCK_ITEMS);
  const [reports, setReports] = useState(MOCK_REPORTS);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [chartWidth, setChartWidth] = useState(600);
  const [dateRange, setDateRange] = useState('month');

  // Ajuster la largeur du graphique en fonction de la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setChartWidth(350);
      } else {
        setChartWidth(600);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simuler une génération de rapport
  const handleGenerateReport = (reportId) => {
    // Dans une application réelle, cela déclencherait une API pour générer le rapport
    const updatedReports = reports.map(report => {
      if (report.id === reportId) {
        return {
          ...report,
          lastGenerated: new Date().toISOString().slice(0, 10)
        };
      }
      return report;
    });
    
    setReports(updatedReports);
    alert('Rapport généré avec succès. Le téléchargement va démarrer.');
  };

  // Générer un graphique en barres simple
  const renderBarChart = (data, valueKey = 'value', labelKey = 'name') => {
    const maxValue = Math.max(...data.map(item => item[valueKey]));
    
    return (
      <div className="mt-4">
        {data.map((item, index) => (
          <div key={index} className="mb-3">
            <div className="flex items-center">
              <span className="text-sm font-medium w-40 truncate">{item[labelKey]}</span>
              <div className="flex-1 ml-2">
                <div className="flex items-center">
                  <div 
                    className="bg-primary-200 h-4 rounded"
                    style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
                  />
                  <span className="ml-2 text-sm text-gray-600">{typeof item[valueKey] === 'number' && item[valueKey].toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Composant de statistique avec icône
  const StatCard = ({ title, value, icon: Icon, trend, color = 'primary' }) => (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-xl font-semibold text-gray-900">{value}</p>
          {trend && (
            <p className={`mt-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{trend}% par rapport au mois dernier
            </p>
          )}
        </div>
        <div className={`bg-${color}-100 p-3 rounded-full`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
      {/* Onglets */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => setSelectedTab('overview')}
            className={`${
              selectedTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setSelectedTab('restock')}
            className={`${
              selectedTab === 'restock'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
          >
            Réapprovisionnement
          </button>
          <button
            onClick={() => setSelectedTab('reports')}
            className={`${
              selectedTab === 'reports'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`}
          >
            Rapports
          </button>
        </nav>
      </div>

      {/* Vue d'ensemble */}
      {selectedTab === 'overview' && (
        <div>
          {/* Filtres temporels */}
          <div className="flex justify-end mb-6">
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setDateRange('month')}
                className={`${
                  dateRange === 'month'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } px-4 py-2 text-sm font-medium rounded-l-md border border-gray-300`}
              >
                Mois
              </button>
              <button
                onClick={() => setDateRange('quarter')}
                className={`${
                  dateRange === 'quarter'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } px-4 py-2 text-sm font-medium border-t border-b border-r border-gray-300`}
              >
                Trimestre
              </button>
              <button
                onClick={() => setDateRange('year')}
                className={`${
                  dateRange === 'year'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                } px-4 py-2 text-sm font-medium rounded-r-md border-t border-b border-r border-gray-300`}
              >
                Année
              </button>
            </div>
          </div>

          {/* Statistiques principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard 
              title="Valeur totale du stock" 
              value={`${stats.totalValue.toLocaleString()} €`} 
              icon={CurrencyEuroIcon} 
              trend={1.2} 
            />
            <StatCard 
              title="Nombre de produits" 
              value={stats.itemCount} 
              icon={ChartBarIcon} 
            />
            <StatCard 
              title="Produits en stock faible" 
              value={stats.lowStockItems} 
              icon={ExclamationCircleIcon} 
              color="yellow"
            />
            <StatCard 
              title="Produits en rupture" 
              value={stats.outOfStockItems} 
              icon={ExclamationCircleIcon} 
              color="red"
            />
          </div>

          {/* Graphiques et visualisations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Répartition par catégorie</h3>
              <p className="text-sm text-gray-500">Valeur du stock par catégorie de produits</p>
              
              {renderBarChart(stats.topSellingCategories)}
              
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-500">
                  Valeur totale: <span className="font-semibold text-gray-700">{stats.totalValue.toLocaleString()} €</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900">Rotation des stocks</h3>
              <p className="text-sm text-gray-500">Répartition des produits par vitesse de rotation</p>
              
              <div className="mt-4">
                {stats.stockRotation.map((item, index) => (
                  <div key={index} className="flex justify-between items-center mb-3">
                    <div className="flex items-center">
                      <div 
                        className={`w-3 h-3 rounded-full mr-2 ${
                          index === 0 ? 'bg-green-500' : index === 1 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {item.count} produits · {item.value.toLocaleString()} €
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 flex justify-center">
                <div className="w-48 h-48 rounded-full border-8 border-green-500 relative flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full border-8 border-yellow-500 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-full border-8 border-red-500 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Total</div>
                        <div className="font-bold">{stats.itemCount}</div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 text-xs font-semibold text-green-600">{stats.stockRotation[0].count}</div>
                  <div className="absolute top-14 right-14 text-xs font-semibold text-yellow-600">{stats.stockRotation[1].count}</div>
                  <div className="absolute top-24 right-24 text-xs font-semibold text-red-600">{stats.stockRotation[2].count}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900">Évolution de la valeur du stock</h3>
            <p className="text-sm text-gray-500">Valeur totale des stocks sur les 6 derniers mois</p>
            
            <div className="mt-6">
              <div className="relative" style={{ height: '200px' }}>
                {/* Axe Y */}
                <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500">
                  <span>50K €</span>
                  <span>45K €</span>
                  <span>40K €</span>
                  <span>35K €</span>
                  <span>30K €</span>
                </div>
                
                <div className="ml-10 h-full flex items-end">
                  {stats.monthlyTrends.map((item, index) => (
                    <div key={index} className="flex flex-col items-center mx-4">
                      <div
                        className="w-10 bg-primary-500 rounded-t"
                        style={{ 
                          height: `${((item.value - 30000) / 20000) * 180}px`,
                          transition: 'height 0.3s ease'
                        }}
                      />
                      <div className="mt-2 text-xs text-gray-500">{item.month}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Réapprovisionnement */}
      {selectedTab === 'restock' && (
        <div>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:px-6 flex justify-between">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Réapprovisionnement suggéré</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Produits nécessitant un réapprovisionnement basé sur les niveaux de stock minimum
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Générer commande
              </button>
            </div>
            <div className="border-t border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produit
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock actuel
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Seuil minimum
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité suggérée
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière commande
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {restockItems.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">{item.sku}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.category}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.current === 0 
                            ? 'bg-red-100 text-red-800' 
                            : item.current < item.minimum 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {item.current}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.minimum}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          min="1"
                          value={item.suggested}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setRestockItems(
                              restockItems.map(ri => 
                                ri.id === item.id ? { ...ri, suggested: value } : ri
                              )
                            );
                          }}
                          className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-20 sm:text-sm border-gray-300 rounded-md"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.lastOrder}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Rapports */}
      {selectedTab === 'reports' && (
        <div>
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {reports.map((report) => (
                <li key={report.id}>
                  <div className="px-4 py-5 sm:px-6 flex justify-between">
                    <div className="flex items-start">
                      <div className="p-2 bg-primary-100 rounded-md">
                        <DocumentChartBarIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                      </div>
                      <div className="ml-4">
                        <h4 className="text-lg font-medium text-gray-900">{report.name}</h4>
                        <p className="mt-1 text-sm text-gray-500">{report.description}</p>
                        <div className="mt-2 text-xs text-gray-500 flex items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                            {report.format}
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1 text-gray-400" />
                            Dernière génération: {report.lastGenerated}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => handleGenerateReport(report.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
                        Générer
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Rapport personnalisé</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Créez un rapport personnalisé en sélectionnant les paramètres ci-dessous
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="report-type" className="block text-sm font-medium text-gray-700">
                    Type de rapport
                  </label>
                  <select
                    id="report-type"
                    name="report-type"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="inventory-valuation">Valorisation des stocks</option>
                    <option value="stock-rotation">Rotation des stocks</option>
                    <option value="low-stock">Produits en stock faible</option>
                    <option value="price-history">Historique des prix</option>
                  </select>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="date-range" className="block text-sm font-medium text-gray-700">
                    Période
                  </label>
                  <select
                    id="date-range"
                    name="date-range"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="current">Situation actuelle</option>
                    <option value="last-month">Dernier mois</option>
                    <option value="last-quarter">Dernier trimestre</option>
                    <option value="last-year">Dernière année</option>
                    <option value="custom">Période personnalisée</option>
                  </select>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700">
                    Filtrer par catégorie
                  </label>
                  <select
                    id="category-filter"
                    name="category-filter"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="">Toutes les catégories</option>
                    <option value="planches">Planches</option>
                    <option value="voiles">Voiles</option>
                    <option value="combinaisons">Combinaisons</option>
                    <option value="accessoires">Accessoires</option>
                  </select>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="format" className="block text-sm font-medium text-gray-700">
                    Format
                  </label>
                  <select
                    id="format"
                    name="format"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  >
                    <option value="excel">Excel (.xlsx)</option>
                    <option value="pdf">PDF</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Générer le rapport
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryAnalytics; 